import { useSelector } from "react-redux";
import {
  selectMap,
  selectVersion,
  selectiDB,
} from "../../features/counter/counterSlice";
import { MenuItem } from "./MenuItem";
import style from "./GuidelineNav.module.scss";
import icons from "../../scss/guidelineicons.module.scss";
import {
  filterContributorLinks,
  filterMenuItems,
  filterResourceLinks,
} from "./filterMenuItems";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useMemo, useState, useEffect } from "react";
import generateMenuFromXml, {
  extractNewAnddeclaration,
} from "./menuGeneration";
import LoginAlertDialog from "../../features/appMode/LoginAlertDialog";
import useAppModernisation from "../../hooks/useAppModernisation";

const specialCases = {
  Palliative_Care: "https://www.tg.org.au/content-updates/palliativecare",
  Rheumatology: "https://www.tg.org.au/content-updates/rheumatology",
  Toxicology_and_Toxinology:
    "https://www.tg.org.au/content-updates/toxicologyandtoxinology",
  Cardiovascular: "https://www.tg.org.au/content-updates/cardiovascular",
  Psychotropic: "https://www.tg.org.au/content-updates/psychotropic/",
  Antibiotic:
    "https://www.tg.org.au/products/therapeutic-guidelines/updates/antibiotic/",
};

const GuidelineNav = ({ glObj }) => {
  const dartMap = useSelector(selectMap);
  const ccmsversion = useSelector(selectVersion);
  const iDB = useSelector(selectiDB);
  const navigate = useNavigate();
  const [menuDartMap, setMenuDartMap] = useState(null);
  const [whatsNewLink, setWhatsNewLink] = useState("");
  const [doiLink, setDoiLink] = useState("");
  const { pathname } = useLocation();
  const { isAppMode } = useAppModernisation();

  useEffect(() => {
    setMenuDartMap(dartMap);
  }, [glObj?.glPage, dartMap]);

  useEffect(() => {
    if (!glObj?.glPage) return;

    const fetchContentForLinks = async () => {
      const path =
        glObj.glPage === "Palliative_Care"
          ? "Palliative_Care/palliative_care.dita"
          : `${glObj.glPage}/${glObj.glPage}.dita`;

      try {
        const localData = await iDB.read(path);
        if (localData) {
          const link = extractNewAnddeclaration(localData);
          setWhatsNewLink(
            specialCases[glObj.glPage] || link.whatsNewLink || "",
          );
          setDoiLink(link.declarationLink || "");
          return;
        }

        const res = await fetch(
          `https://ccmsfiles.tg.org.au/${ccmsversion?.selected}/${path}`,
        );
        if (!res.ok) throw new Error("Failed to fetch remote DITA file");
        const raw = await res.text();
        const link = extractNewAnddeclaration(raw);
        setWhatsNewLink(specialCases[glObj.glPage] || link.whatsNewLink || "");
        setDoiLink(link.declarationLink || "");
      } catch (err) {
        console.error("Error fetching DOI / What's New content:", err);
        setWhatsNewLink("");
        setDoiLink("");
      }
    };

    fetchContentForLinks();
  }, [glObj?.glPage, iDB, ccmsversion]);

  const {
    menuItems = { title: "", items: [] },
    rootGuideline = false,
    glInfo = {},
  } = useMemo(() => {
    if (!glObj || !menuDartMap) return {};
    return (
      filterMenuItems({
        glObj,
        menuJson: generateMenuFromXml(menuDartMap),
      }) || {}
    );
  }, [glObj, menuDartMap]);

  const tablesAndFiguresMenu = useMemo(
    () =>
      menuItems?.items?.filter(
        (props) =>
          props.msPath?.includes("-tables-boxes-figures") &&
          !props.id?.includes("quicklinks"),
      ) ?? [],
    [menuItems?.items],
  );

  const topicMenus = useMemo(
    () =>
      menuItems?.items?.filter(
        (props) => !props.msPath?.includes("-tables-boxes-figures"),
      ) ?? [],
    [menuItems?.items],
  );

  const addtionalResourceItems = useMemo(() => {
    const items = [
      { key: "Whats_New", label: "What's new" },
      { key: "expert-groups", label: "Expert group" },
      { key: "acknowledgements", label: "Acknowledgements" },
      { key: "Declaration_of_Interest", label: "Declaration of interests" },
    ];

    return items
      .map(({ key, label }) => {
        if (key === "acknowledgements" || key === "expert-groups") {
          return {
            title: label,
            msPath: `/resources/${glObj?.glPage?.replaceAll(" ", "_")}/${key}`,
          };
        }

        if (key === "Whats_New") {
          if (
            whatsNewLink &&
            whatsNewLink?.startsWith("https://www.tg.org.au")
          ) {
            return {
              title: label,
              msPath: whatsNewLink,
              isExternal: true,
            };
          } else {
            const slicedLink = whatsNewLink.replace("../", "");
            return {
              title: label,
              msPath: `https://ccmsfiles.tg.org.au/${ccmsversion?.selected}/${slicedLink}`,
              isExternal: true,
            };
          }
        }

        if (key === "Declaration_of_Interest") {
          const slicedLink = doiLink?.replace("../", "");
          return {
            title: label,
            msPath: `https://ccmsfiles.tg.org.au/${ccmsversion?.selected}/${slicedLink}`,
            isExternal: true,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [glObj?.glPage, whatsNewLink, doiLink, ccmsversion]);

  const resourceItems = useMemo(
    () =>
      !pathname.includes("app/nav/quicklinks") && [
        ...tablesAndFiguresMenu,
        ...filterResourceLinks(addtionalResourceItems),
      ],
    [tablesAndFiguresMenu, addtionalResourceItems],
  );

  const contributorItems = useMemo(
    () =>
      !pathname.includes("app/nav/quicklinks") && [
        ...filterContributorLinks(addtionalResourceItems),
      ],
    [addtionalResourceItems],
  );

  if (!glObj) return null;

  const chapterRoot = glObj.glPage.replaceAll(" ", "_");
  const iconClass = classNames(
    "squareIcon",
    { title: rootGuideline, subtitle: !rootGuideline },
    glInfo.iconClassSuffix,
  );
  const iconClassMobile = classNames(
    icons.squareIcon,
    { [icons.title]: rootGuideline, [icons.subtitle]: !rootGuideline },
    icons[glInfo.iconClassSuffix],
  );

  const goTopic = () => {
    isAppMode
      ? navigate(`/app/nav/${chapterRoot}`)
      : navigate(`/guidelines/${chapterRoot}`);
  };
  
  return (
    <>
      <LoginAlertDialog />
      <Helmet>
        <title>{menuItems.title}</title>
      </Helmet>

      <div className={style.iconContainer}>
        <div
          className={isAppMode ? iconClass : iconClassMobile}
          role="img"
        ></div>
        {!rootGuideline && (
          <div className="size-22 semibold" onClick={goTopic}>
               {glInfo.linkText}&nbsp;
               {glInfo.linkText && <span className="size-14 tg-icons-Carat"></span>}
          </div>
        )}
      </div>

      <h1 className={`size-36 regular ${style.title}`}>{menuItems.title}</h1>

      {rootGuideline && (
        <div className={`size-16 bold ${style.subtitle}`}>Topics</div>
      )}

      <div>
        {topicMenus.map((props, index) => (
          <MenuItem {...props} key={`menu-${index}`} base={glObj.glPage} />
        ))}
      </div>

      {rootGuideline && resourceItems.length > 0 && (
        <>
          <div className={`size-16 bold ${style.subtitle}`}>Resources</div>
          <div>
            {resourceItems.map((props, index) => (
              <MenuItem
                {...props}
                key={`resource-${index}`}
                base={glObj.glPage}
              />
            ))}
          </div>
        </>
      )}
      {rootGuideline && contributorItems.length > 0 && (
        <>
          <div className={`size-16 bold ${style.subtitle}`}>Contributors</div>
          <div>
            {contributorItems.map((props, index) => (
              <MenuItem
                {...props}
                key={`resource-${index}`}
                base={glObj.glPage}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default GuidelineNav;
